import type { TFunction } from 'i18next'

export const TIME_WINDOW_KINDS = ['FREE', 'SPEED', 'ACCESS'] as const
export type TimeWindowKind = (typeof TIME_WINDOW_KINDS)[number]

export const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7] as const
export const MAX_TIME_WINDOWS = 24
export const CLOCK_FORMAT = 'HH:mm'

export interface TimeWindow {
  kind: TimeWindowKind
  /** 1 = dushanba ... 7 = yakshanba */
  days: number[]
  from: string
  to: string
  rateLimit: string | null
  burst: string | null
}

export interface TimeWindowFormRow {
  kind?: TimeWindowKind
  days?: number[]
  from?: string | null
  to?: string | null
  rateLimit?: string | null
  burst?: string | null
}

function normalizedDays(days: number[] | undefined): number[] {
  const chosen = [...new Set(days ?? [])].sort((a, b) => a - b)
  return chosen.length === 0 ? [...WEEK_DAYS] : chosen
}

export function timeWindowDrafts(rows: TimeWindowFormRow[] | undefined): TimeWindow[] {
  return (rows ?? []).flatMap((row) => {
    if (!row.kind || !row.from || !row.to) return []
    const speed = row.kind === 'SPEED'
    return [
      {
        kind: row.kind,
        days: normalizedDays(row.days),
        from: row.from,
        to: row.to,
        rateLimit: speed ? row.rateLimit?.trim() || null : null,
        burst: speed ? row.burst?.trim() || null : null,
      },
    ]
  })
}

export function timeWindowRange(window: Pick<TimeWindow, 'from' | 'to'>): string {
  return `${window.from}–${window.to}`
}

interface DayRun {
  first: number
  last: number
}

function dayRuns(days: number[]): DayRun[] {
  const runs: DayRun[] = []
  for (const day of days) {
    const run = runs.at(-1)
    if (run && run.last === day - 1) run.last = day
    else runs.push({ first: day, last: day })
  }
  return runs
}

export function timeWindowDays(t: TFunction, days: number[]): string | null {
  const chosen = normalizedDays(days)
  if (chosen.length === WEEK_DAYS.length) return null
  const name = (day: number) => t(`catalog.policy.window.day.${day}`)
  return dayRuns(chosen)
    .map(({ first, last }) => {
      if (first === last) return name(first)
      return `${name(first)}${last - first === 1 ? ', ' : '–'}${name(last)}`
    })
    .join(', ')
}

export function timeWindowDetails(t: TFunction, window: TimeWindow): string[] {
  return [window.rateLimit, timeWindowDays(t, window.days)].filter(
    (detail): detail is string => detail !== null,
  )
}
